import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, ChevronDown, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import ExportButton from "@/components/ExportButton";
import DeleteButton from "@/components/DeleteButton";
import { useAuth } from "@/context/AuthContext";

const MATS = [
  "grey_cement", "white_cement", "sand", "grit_10mm", "grit_20mm",
  "marble_dust", "marble_chips_5mm", "marble_chips_10mm", "marble_pieces",
  "glass_chips", "glass_powder",
  "red_color", "black_color", "yellow_color", "chocolate_color", "green_color", "blue_color",
  "ar_glass_fiber", "hardner", "water", "other",
];
const UNITS = ["kg", "bags", "liters", "pcs"];
const KINDS = ["purchase", "opening"];
const prettyMat = (m) => (m || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Purchases() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    kind: "purchase", material: "grey_cement",
    quantity: "", unit: "bags", supplier: "", rate_per_unit: "", notes: "",
  });

  const load = () => api.get("/material-purchases").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/material-purchases/template.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "sango_purchases_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Template download failed"); }
  };

  const uploadCSV = async (file) => {
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/material-purchases/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      if (data.imported > 0) toast.success(`Imported ${data.imported} row${data.imported === 1 ? "" : "s"}`);
      if (data.errors?.length) toast.error(`${data.errors.length} row${data.errors.length === 1 ? "" : "s"} skipped`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const save = async () => {
    if (!form.quantity) { toast.error("Quantity required"); return; }
    try {
      await api.post("/material-purchases", {
        ...form, quantity: parseFloat(form.quantity),
        rate_per_unit: form.rate_per_unit ? parseFloat(form.rate_per_unit) : null,
        added_by: "",
      });
      toast.success(form.kind === "opening" ? "Opening stock recorded" : "Purchase recorded");
      setOpen(false);
      setForm({ ...form, quantity: "", rate_per_unit: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A]">Inbound · Raw Material</div>
            <h1 className="font-display text-4xl tracking-tighter font-black">Purchases & Opening Stock</h1>
            <p className="text-[#6E6E6A] text-sm mt-2">Every entry increases available material stock (used in Stock page).</p>
          </div>
          <div className="flex gap-2">
            <ExportButton module="purchases" />
            <Button variant="outline" onClick={downloadTemplate} data-testid="csv-template" className="gap-2">
              <Download className="w-4 h-4" /> Template
            </Button>
            <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) setImportResult(null); }}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="csv-import" className="gap-2">
                  <Upload className="w-4 h-4" /> Import CSV
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Bulk import purchases / opening stock</DialogTitle></DialogHeader>
                <div className="space-y-3 text-sm">
                  <p className="text-[#6E6E6A]">Upload a CSV with columns: <code className="text-[11px]">date, material, quantity, unit, kind, supplier, rate_per_unit, notes</code>.</p>
                  <p className="text-[#6E6E6A]"><strong>kind</strong> = <code>opening</code> or <code>purchase</code>. <strong>material</strong> keys use underscores (e.g. <code>grey_cement</code>, <code>marble_chips_5mm</code>).</p>
                  <Button variant="ghost" size="sm" onClick={downloadTemplate} className="gap-2 text-xs -mx-2"><Download className="w-3 h-3" /> Download sample template</Button>
                  <input
                    type="file" accept=".csv,text/csv"
                    onChange={(e) => uploadCSV(e.target.files?.[0])}
                    data-testid="csv-file"
                    className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#1C1C1A] file:text-white hover:file:bg-black cursor-pointer"
                    disabled={importing}
                  />
                  {importing && <p className="text-xs text-[#6E6E6A]">Uploading…</p>}
                  {importResult && (
                    <div className="mt-3 p-3 bg-[#F1F1EF] border border-[#D8D8D5] rounded-md text-xs">
                      <div>Total rows: <strong>{importResult.total_rows}</strong></div>
                      <div className="text-[#5B7B61]">Imported: <strong>{importResult.imported}</strong></div>
                      {importResult.errors?.length > 0 && (
                        <>
                          <div className="text-[#C85A42] mt-1">Skipped: <strong>{importResult.errors.length}</strong></div>
                          <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                            {importResult.errors.slice(0, 10).map((er, i) => (
                              <div key={i} className="text-[10px] text-[#6E6E6A]">Line {er.line}: {er.error}</div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-purchase" className="bg-[#1C1C1A] hover:bg-black text-white"><Plus className="w-4 h-4 mr-2" /> New entry</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add stock in</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                  <div>
                    <Label>Type</Label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="outline" className="w-full justify-between mt-2 capitalize">{form.kind}<ChevronDown className="w-4 h-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent>{KINDS.map(k => <DropdownMenuItem key={k} onClick={() => setForm({ ...form, kind: k })} className="capitalize">{k}</DropdownMenuItem>)}</DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="col-span-2">
                    <Label>Material</Label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="outline" className="w-full justify-between mt-2" data-testid="pur-material">{prettyMat(form.material)}<ChevronDown className="w-4 h-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent className="max-h-72 overflow-y-auto">{MATS.map(m => <DropdownMenuItem key={m} onClick={() => setForm({ ...form, material: m })}>{prettyMat(m)}</DropdownMenuItem>)}</DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div><Label>Quantity</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="pur-qty" /></div>
                  <div>
                    <Label>Unit</Label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="outline" className="w-full justify-between mt-2">{form.unit}<ChevronDown className="w-4 h-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent>{UNITS.map(u => <DropdownMenuItem key={u} onClick={() => setForm({ ...form, unit: u })}>{u}</DropdownMenuItem>)}</DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div><Label>Supplier</Label><Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></div>
                  <div><Label>Rate ₹/unit</Label><Input type="number" value={form.rate_per_unit} onChange={(e) => setForm({ ...form, rate_per_unit: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                </div>
                <Button onClick={save} data-testid="pur-save" className="mt-4 bg-[#1C1C1A] hover:bg-black text-white">Save</Button>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card className="mt-8 border border-[#D8D8D5] shadow-none rounded-md overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Material</TableHead>
              <TableHead>Qty</TableHead><TableHead>Rate</TableHead><TableHead>Amount</TableHead>
              <TableHead>Supplier</TableHead><TableHead>By</TableHead>
              {user?.role === "admin" && <TableHead></TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={user?.role === "admin" ? 9 : 8} className="text-center text-[#6E6E6A] py-8">No purchases yet — add an opening stock entry to start tracking.</TableCell></TableRow>}
              {rows.map(r => (
                <TableRow key={r.id}>
                  <TableCell>{r.date}</TableCell>
                  <TableCell><Badge variant="outline" className={r.kind === "opening" ? "border-[#5B7B61]/40 text-[#5B7B61]" : "border-[#1C1C1A]/40 text-[#1C1C1A]"}>{r.kind}</Badge></TableCell>
                  <TableCell>{prettyMat(r.material)}</TableCell>
                  <TableCell className="font-medium">{r.quantity} {r.unit}</TableCell>
                  <TableCell>{r.rate_per_unit ? `₹${r.rate_per_unit}` : "—"}</TableCell>
                  <TableCell>{r.total_amount ? `₹${r.total_amount.toLocaleString('en-IN')}` : "—"}</TableCell>
                  <TableCell className="text-xs">{r.supplier || "—"}</TableCell>
                  <TableCell className="text-xs">{r.added_by_name}</TableCell>
                  {user?.role === "admin" && (
                    <TableCell><DeleteButton endpoint={`/material-purchases/${r.id}`} label="purchase" onDone={load} testid={`del-pur-${r.id}`} /></TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
